import React, { useState } from 'react'
import { Button, ErrorMessage } from '@lightspeed/design-system-react'
import VendNumber, { vn } from 'vend-number'

import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { T, useT } from '@transifex/react'
import { isCorrectlyRounded } from '../../../../helpers/isCorrectlyRounded'
import { getQuickCashSuggestions } from '../../../quick-cash-payment-modal/quick-cash-suggestions'
import { HandlePaymentFunction, RoundedNumber } from '../../CashPaymentModal'
import { useFormatCurrencyValue } from '../../../../sale/summary-panel/useFormatCurrencyValue'

interface ActionsProps {
  amountToPay: RoundedNumber
  currencySymbol: string
  handlePayment: HandlePaymentFunction
  rounding: string | VendNumber
}

// Component
export const Actions: React.FC<ActionsProps> = ({
  handlePayment,
  amountToPay,
  currencySymbol,
  rounding,
}) => {
  const t = useT()
  const formatCurrencyValue = useFormatCurrencyValue()

  // Tender Input hook
  const [tenderInput, setTenderInput] = useState('')
  // Constants
  const roundedAmountToPay = vn(amountToPay.rounded)
  const suggestions = getQuickCashSuggestions(
    roundedAmountToPay.toNumber()
  ).slice(0, 2)
  const tenderInputIsEmpty = tenderInput.trim() === ''

  const getErrorMessage = () => {
    if (isNaN(Number(tenderInput))) {
      return t('Please enter a number.')
    } else if (roundedAmountToPay.gt(vn(tenderInput))) {
      const val = formatCurrencyValue(roundedAmountToPay.toString())
      return t(`Please enter an amount equal to or greater than {val}.`, {
        val,
      })
    } else if (!isCorrectlyRounded(tenderInput, rounding)) {
      const val = formatCurrencyValue(vn(rounding).toString())
      return t(`Please round the amount to a {val} value.`, { val })
    } else {
      return ''
    }
  }

  // Error constants
  const errorMessage = getErrorMessage()
  const showError = !tenderInputIsEmpty && errorMessage !== ''

  const handleMainButtonClick = () => {
    if (tenderInputIsEmpty) {
      handlePayment(roundedAmountToPay, 'suggestion')
    } else if (!showError) {
      handlePayment(vn(tenderInput), 'manual_click')
    }
  }

  const handleKeyDown = (key: string) => {
    if (key === 'Enter' && !tenderInputIsEmpty && !showError) {
      handlePayment(vn(tenderInput), 'manual_enter')
    }
  }

  return (
    <>
      <div className="payment-cash-modal-actions">
        <div className="payment-cash-modal-actions-item">
          <div className="vd-field">
            <div className="vd-value">
              <input
                className={`vd-input payment-cash-modal-actions-input ${
                  showError ? 'vd-input--error' : ''
                }`}
                onChange={e => setTenderInput(e.target.value)}
                onKeyDown={({ key }) => handleKeyDown(key)}
                value={tenderInput}
                placeholder="0.00"
                autoFocus
                type="number"
                inputMode="decimal"
              />
              <div className="payment-cash-modal-actions-input--currency vd-input-icon vd-input-icon--left vd-input-symbol">
                {currencySymbol}
              </div>
            </div>
          </div>
        </div>
        <div className="payment-cash-modal-actions-item">
          <Button
            jumbo
            block
            variant={tenderInputIsEmpty ? 'supplementary' : 'go'}
            disabled={showError}
            onClick={() => handleMainButtonClick()}
            data-track="collect-cash"
          >
            <CSSTransition
              in={!tenderInputIsEmpty}
              timeout={500}
              classNames="animation"
              unmountOnExit
            >
              <span className="payment-cash-modal-actions-item--collect">
                <T
                  _str="Collect {amount}"
                  amount={formatCurrencyValue(tenderInput)}
                />
              </span>
            </CSSTransition>
            {tenderInputIsEmpty
              ? formatCurrencyValue(roundedAmountToPay.toString())
              : null}
          </Button>
        </div>
        <TransitionGroup component={null}>
          {tenderInputIsEmpty &&
            suggestions.map(suggestion => (
              <CSSTransition
                classNames="animation"
                timeout={500}
                key={suggestion.toNumber()}
              >
                <div className="payment-cash-modal-actions-item payment-cash-modal-actions-item--suggestion">
                  <Button
                    jumbo
                    block
                    variant="supplementary"
                    onClick={() => handlePayment(suggestion, 'suggestion')}
                    data-track="collect-cash-suggestion"
                  >
                    {formatCurrencyValue(suggestion.toString())}
                  </Button>
                </div>
              </CSSTransition>
            ))}
        </TransitionGroup>
      </div>

      <CSSTransition
        in={showError}
        timeout={500}
        classNames="animation"
        unmountOnExit
      >
        <div
          className="payment-cash-modal-actions-input-error"
          data-testid="actions-error-message"
        >
          <ErrorMessage>{errorMessage}</ErrorMessage>
        </div>
      </CSSTransition>
    </>
  )
}
